import { InsightsReportContract, ReportFilter } from '../../reporting_v2/helpers/build-api-report-contract'
import { ReportEntity } from '../../reporting_v2/enums/report-entity'
import { ReportDateIncrement } from '../../reporting_v2/enums/report-date-increment'
import { DateWindow, IsoDate, isValidIsoDate } from '../math/windows'
import { DailyMeasure, DailyRow } from '../math/series'
import type { OrganizationSubscriberLevelDto } from '../../../dtos/organization-subscriber-level.dto'

type ActionAttribution = NonNullable<InsightsReportContract['action_attribution']>

/*
 * Breakdown combinations are matched as sets against what each scope's document offers.
 *
 * Organization scope offers notifications and prompts per domain, and notifications per domain and
 * notification only together with the notification group, so the notification ranking asks for
 * that combination.
 */
const ORGANIZATION_BY_DOMAIN = ['organization', 'domain']
const ORGANIZATION_BY_DOMAIN_BY_NOTIFICATION = ['organization', 'domain', 'notification', 'notification_group']

/**
 * The name of the date column a day-incremented report adds to each row: the attribution in force
 * with `_time` read as `_date`. The metadata document names only the column for the entity's default
 * attribution, so a request that overrides it has to derive the name itself.
 */
export const dateColumnFor = (actionAttribution: ActionAttribution): string =>
    actionAttribution.replace('_time', '_date')

/*
 * Each request names only the measures it is given, which are the ones the organization's definitions offer: a
 * request for a measure the organization may not read can be refused whole, taking everything else with it.
 */

/**
 * Impressions, clicks and purchases are counted on the day the action happened, which is the day the
 * subscriber's own activity is reported against.
 */
export const engagementSeriesContract = (window: DateWindow, measures: string[]): InsightsReportContract => ({
    entity: ReportEntity.NOTIFICATIONS,
    breakdowns: ORGANIZATION_BY_DOMAIN,
    fields: ['domain.id', 'domain.display_name', 'domain.name', ...measures],
    date_range: window,
    date_increment: ReportDateIncrement.DAY,
    action_attribution: 'event_time',
    filters: [],
})

/** Sends are counted on the day they went out, not on the day their engagement happened. */
export const sendsSeriesContract = (window: DateWindow, measures: string[]): InsightsReportContract => ({
    entity: ReportEntity.NOTIFICATIONS,
    breakdowns: ORGANIZATION_BY_DOMAIN,
    fields: ['domain.id', ...measures],
    date_range: window,
    date_increment: ReportDateIncrement.DAY,
    action_attribution: 'send_time',
    filters: [],
})

export const acquisitionSeriesContract = (window: DateWindow, measures: string[]): InsightsReportContract => ({
    entity: ReportEntity.PROMPTS,
    breakdowns: ORGANIZATION_BY_DOMAIN,
    fields: ['domain.id', ...measures],
    date_range: window,
    date_increment: ReportDateIncrement.DAY,
    filters: [],
})

/** An empty list means every domain in the organization, so no filter is sent. */
const organizationDomainFilter = (domainIds: number[]): ReportFilter[] =>
    domainIds.length ? [{ field: 'domain.id', operator: 'in', value: domainIds }] : []

export type RankingFloor = { measure: string; minimum: number }

export const topNotificationsContract = (
    range: DateWindow,
    domainIds: number[],
    rankBy: string,
    floor: RankingFloor | undefined,
    measures: string[],
): InsightsReportContract => ({
    entity: ReportEntity.NOTIFICATIONS,
    breakdowns: ORGANIZATION_BY_DOMAIN_BY_NOTIFICATION,
    fields: [
        'domain.id',
        'domain.display_name',
        'notification_group.id',
        'notification.id',
        'notification_title',
        'notification_body',
        'notification_image_url',
        'notification_source',
        'notification_scheduled_time',
        'notification_scheduled_time_zone',
        ...measures,
    ],
    date_range: range,
    date_increment: ReportDateIncrement.LIFETIME,
    action_attribution: 'event_time',
    order: { field: rankBy, direction: 'desc' },
    pagination: { page: 1, limit: 10 },
    filters: [
        ...organizationDomainFilter(domainIds),
        ...(floor ? [{ field: floor.measure, operator: 'gte', value: floor.minimum } as ReportFilter] : []),
    ],
})

/**
 * A row of the top notifications report as the response carries it: the columns named by prefix arrive nested
 * (`notification.title`), and the ones the contract requests by flat name may arrive flat. The response is not
 * trusted to hold every column or to hold numbers, so the measures are `unknown`.
 */
export type TopNotificationReportRow = {
    notification?: {
        id?: number | string
        title?: string
        body?: string
        image_url?: string | null
        source?: string
        scheduled_time?: string
        scheduled_time_zone?: string
    }
    domain?: { id?: unknown; display_name?: string }
    notification_source?: string
    notification_scheduled_time?: string
    notification_scheduled_time_zone?: string
    impressions?: unknown
    clicks?: unknown
    ctr_decimal?: unknown
    purchases?: unknown
    purchase_amount?: unknown
    purchase_rate_decimal?: unknown
}

/** Where each source's rows keep the domain and the day, as the response carries them. */
export type ResolvedShape = {
    domainId: string[]
    engagementDate: string
    sendsDate: string
    acquisitionDate: string
}

/**
 * Rows are JSON, so a dotted field lands nested (`domain.id` is `row.domain.id`) while the date
 * column is flat. Prompts count against the event day by default.
 */
export const ORG_DASHBOARD_RESPONSE_SHAPE: ResolvedShape = {
    domainId: ['domain', 'id'],
    engagementDate: dateColumnFor('event_time'),
    sendsDate: dateColumnFor('send_time'),
    acquisitionDate: dateColumnFor('event_time'),
}

/*
 * A report row as it is read at the boundary: the domain and day it belongs to, validated, and each measure as the
 * response carried it. Measures stay `unknown` because the response is not trusted to hold numbers, or to hold every
 * column that was not requested.
 */
type ReportRowKey = { domainId: number; date: IsoDate }
export type EngagementReportRow = ReportRowKey & {
    impressions: unknown
    clicks: unknown
    purchases: unknown
    purchase_amount: unknown
}
export type SendsReportRow = ReportRowKey & { notifications_sent: unknown }
export type AcquisitionReportRow = ReportRowKey & { permissions_shown: unknown; subscriptions: unknown }

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

const readPath = (row: unknown, path: string[]): unknown =>
    path.reduce<unknown>((value, key) => (isRecord(value) ? value[key] : undefined), row)

/** A number, or a string holding one; null for anything else, including null, an empty string and a non-finite value. */
const finiteNumberOf = (value: unknown): number | null => {
    const number =
        typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN
    return Number.isFinite(number) ? number : null
}

const dayOf = (value: unknown): string => (typeof value === 'string' ? value.slice(0, 10) : '')

/** Rows that cannot be placed on a domain and a calendar day are dropped: they belong to no one's day. */
const parseReportRows = <Measures extends Record<string, unknown>>(
    rows: unknown[],
    domainIdPath: string[],
    dateColumn: string,
    measuresOf: (row: Record<string, unknown>) => Measures,
): Array<ReportRowKey & Measures> =>
    rows.flatMap((row) => {
        if (!isRecord(row)) return []
        const domainId = finiteNumberOf(readPath(row, domainIdPath))
        const date = dayOf(row[dateColumn])
        return domainId === null || !isValidIsoDate(date) ? [] : [{ domainId, date, ...measuresOf(row) }]
    })

const engagementReportRows = (rows: unknown[], shape: ResolvedShape): EngagementReportRow[] =>
    parseReportRows(rows, shape.domainId, shape.engagementDate, (row) => ({
        impressions: row.impressions,
        clicks: row.clicks,
        purchases: row.purchases,
        purchase_amount: row.purchase_amount,
    }))

const sendsReportRows = (rows: unknown[], shape: ResolvedShape): SendsReportRow[] =>
    parseReportRows(rows, shape.domainId, shape.sendsDate, (row) => ({ notifications_sent: row.notifications_sent }))

const acquisitionReportRows = (rows: unknown[], shape: ResolvedShape): AcquisitionReportRow[] =>
    parseReportRows(rows, shape.domainId, shape.acquisitionDate, (row) => ({
        permissions_shown: row.permissions_shown,
        subscriptions: row.subscriptions,
    }))

/**
 * One DailyRow per (domain, day) present in any source. A day missing from a flow source contributes zeros; a
 * day without a subscriber sample keeps a null level rather than a zero. Rows for the same domain and day add
 * up, and a measure a row does not carry as a number adds nothing to the day.
 */
export const rowsToDailyRows = (
    engagement: unknown[],
    sends: unknown[],
    acquisition: unknown[],
    levels: OrganizationSubscriberLevelDto[],
    shape: ResolvedShape,
): DailyRow[] => {
    const rowsByDomainAndDate = new Map<string, DailyRow>()
    const rowFor = (domainId: number, date: string): DailyRow => {
        const key = `${domainId}|${date}`
        let row = rowsByDomainAndDate.get(key)
        if (!row) {
            row = {
                domainId,
                date,
                impressions: 0,
                clicks: 0,
                sends: 0,
                purchases: 0,
                purchaseAmount: 0,
                permissionsShown: 0,
                subscriptions: 0,
                subscribers: null,
            }
            rowsByDomainAndDate.set(key, row)
        }
        return row
    }
    const addReported = (dailyRow: DailyRow, measure: DailyMeasure, reported: unknown): void => {
        const amount = finiteNumberOf(reported)
        if (amount !== null) dailyRow[measure] += amount
    }

    for (const reportRow of engagementReportRows(engagement, shape)) {
        const dailyRow = rowFor(reportRow.domainId, reportRow.date)
        addReported(dailyRow, 'impressions', reportRow.impressions)
        addReported(dailyRow, 'clicks', reportRow.clicks)
        addReported(dailyRow, 'purchases', reportRow.purchases)
        addReported(dailyRow, 'purchaseAmount', reportRow.purchase_amount)
    }
    for (const reportRow of sendsReportRows(sends, shape)) {
        addReported(rowFor(reportRow.domainId, reportRow.date), 'sends', reportRow.notifications_sent)
    }
    for (const reportRow of acquisitionReportRows(acquisition, shape)) {
        const dailyRow = rowFor(reportRow.domainId, reportRow.date)
        addReported(dailyRow, 'permissionsShown', reportRow.permissions_shown)
        addReported(dailyRow, 'subscriptions', reportRow.subscriptions)
    }
    for (const level of levels) {
        rowFor(level.domain_id, dayOf(level.date)).subscribers = level.subscribers
    }

    return [...rowsByDomainAndDate.values()].sort(
        (first, second) => first.domainId - second.domainId || first.date.localeCompare(second.date),
    )
}
